import type { LoanApplicationData } from "../hooks/useNbiForm";

type Applicant = LoanApplicationData["applicants"][number];
type Income = LoanApplicationData["incomes"][number];

const SESSION_KEY_PREFIX = "nbi:incomePageVisited";

const getSessionKey = (appId?: string | number): string => {
    if (appId) {
        return `${SESSION_KEY_PREFIX}:${appId}`;
    }
    return SESSION_KEY_PREFIX;
};

interface IncomePageVisitedData {
    visited: boolean;
    applicantIds: string[];
}

export const getApplicantsWithoutIncome = (applicants: Applicant[], incomes: Income[]): Applicant[] => {
    const applicantsWithIncome = new Set(
        incomes.filter((i) => i.applicantId && i.applicantId !== "__pending__").map((i) => i.applicantId)
    );
    return applicants.filter((a) => !applicantsWithIncome.has(a.id));
};

export const hasApplicantsWithoutIncome = (applicants: Applicant[], incomes: Income[]): boolean => {
    return getApplicantsWithoutIncome(applicants, incomes).length > 0;
};

export const hasVisitedIncomePage = (appId?: string | number, currentApplicantIds?: string[]): boolean => {
    if (typeof window === "undefined") return false;

    const stored = sessionStorage.getItem(getSessionKey(appId));
    if (!stored) return false;

    try {
        const data: IncomePageVisitedData = JSON.parse(stored);
        if (!data.visited) return false;

        if (currentApplicantIds && currentApplicantIds.length > 0) {
            const visitedSet = new Set(data.applicantIds);
            return currentApplicantIds.some((id) => visitedSet.has(id));
        }

        return data.visited;
    } catch {
        return stored === "1";
    }
};

export const markIncomePageVisited = (appId?: string | number, applicantIds?: string[]): void => {
    if (typeof window === "undefined") return;

    const data: IncomePageVisitedData = {
        visited: true,
        applicantIds: applicantIds || [],
    };

    sessionStorage.setItem(getSessionKey(appId), JSON.stringify(data));
};

export const clearIncomePageVisited = (appId?: string | number): void => {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem(getSessionKey(appId));
};

export const getApplicantFullName = (applicant: Applicant, allApplicants?: Applicant[]): string => {
    const givenName = ((applicant as any).givenName || (applicant as any).givenNames || "").trim();
    const familyName = ((applicant as any).familyName || (applicant as any).familyNames || "").trim();
    const fullName = `${givenName} ${familyName}`.trim();

    if (fullName) return fullName;

    if (allApplicants) {
        const idx = allApplicants.findIndex((a) => a.id === applicant.id);
        if (idx !== -1) return `Applicant #${idx + 1}`;
    }

    return "Unknown Applicant";
};
